import { Action } from "redux";
import { AppState } from "./appState";
import ACTION_TYPE from "./ActionTypes";
import { TGridSortModel } from "../constants";
import { GridPaginationModel } from "@mui/x-data-grid";

/** ========= Generic Types ========= */

type PageAction = {
  type: ACTION_TYPE;
  pageObject: GridPaginationModel;
};

type SortAction = {
  type: ACTION_TYPE;
  sortObject: TGridSortModel;
};

/** ========= Factory Helpers ========= */

/**
 * Creates a page action creator and reducer
 */
function createPageStateHandlers(
  type: ACTION_TYPE,
  stateKey: keyof AppState
) {
  const savePageObject = (pageObject: GridPaginationModel): PageAction => ({
    type,
    pageObject,
  });

  const reducer = (state: AppState, action: Action): AppState => {
    if (action.type !== type) return state;
    return {
      ...state,
      [stateKey]: (action as PageAction).pageObject,
    };
  };

  return { savePageObject, reducer };
}

/**
 * Creates a sort action creator and reducer
 */
function createSortStateHandlers(
  type: ACTION_TYPE,
  stateKey: keyof AppState
) {
  const saveSortObject = (sortObject: TGridSortModel): SortAction => ({
    type,
    sortObject,
  });

  const reducer = (state: AppState, action: Action): AppState => {
    if (action.type !== type) return state;
    return {
      ...state,
      [stateKey]: (action as SortAction).sortObject,
    };
  };

  return { saveSortObject, reducer };
}

/** ========= Specific Implementations ========= */

// Overages
export const {
  savePageObject: saveOverageListPageObject,
  reducer: saveOverageListPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.OVERAGE_DATA_TABLE_PAGE_STATE,
  "overageListPageObject"
);

export const {
  saveSortObject: saveOverageListSortObject,
  reducer: saveOverageListSortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.OVERAGE_DATA_TABLE_SORT_STATE,
  "overageSortObjectData"
);

// My Assignments
export const {
  savePageObject: saveMyAssignmentPageObject,
  reducer: saveMyAssignmentPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.MY_ASSIGNMENT_DATA_TABLE_PAGE_STATE,
  "myAssignmentPageObject"
);

export const {
  saveSortObject: saveMyAssignmentSortObject,
  reducer: saveMyAssignmentSortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.MY_ASSIGNMENT_DATA_TABLE_SORT_STATE,
  "myAssignmentSortObjectData"
);

// Shortages
export const {
  savePageObject: saveShortagesPageObject,
  reducer: saveShortagesPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.SHORTAGES_PAGE_STATE,
  "shortagesPageObject"
);

export const {
  saveSortObject: saveShortagesSortObject,
  reducer: saveShortagesSortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.SHORTAGES_SORT_STATE,
  "shortagesSortObject"
);

// Damages
export const {
  savePageObject: saveDamagesPageObject,
  reducer: saveDamagesPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.DAMAGES_PAGE_STATE,
  "damagesPageObject"
);

export const {
  saveSortObject: saveDamagesSortObject,
  reducer: saveDamagesSortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.DAMAGES_SORT_STATE,
  "damagesSortObject"
);

// On Hand Delivery
export const {
  savePageObject: saveOnHandDeliveryPageObject,
  reducer: saveOnHandDeliveryPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.ON_HAND_DELIVERY_PAGE_STATE,
  "onHandDeliveryPageObject"
);

export const {
  saveSortObject: saveOnHandDeliverySortObject,
  reducer: saveOnHandDeliverySortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.ON_HAND_DELIVERY_SORT_STATE,
  "onHandDeliverySortObject"
);

// Search Exception
export const {
  savePageObject: saveSearchExceptionPageObject,
  reducer: saveSearchExceptionPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.SEARCH_EXCEPTION_PAGE_STATE,
  "searchExceptionPageObject"
);

export const {
  saveSortObject: saveSearchExceptionSortObject,
  reducer: saveSearchExceptionSortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.SEARCH_EXCEPTION_SORT_STATE,
  "searchExceptionSortObject"
);

// Misdelivery
export const {
  savePageObject: saveMisdeliveryPageObject,
  reducer: saveMisdeliveryPageObjectReducer,
} = createPageStateHandlers(
  ACTION_TYPE.MISDELIVERY_PAGE_STATE,
  "misdeliveryPageObject"
);

export const {
  saveSortObject: saveMisdeliverySortObject,
  reducer: saveMisdeliverySortObjectReducer,
} = createSortStateHandlers(
  ACTION_TYPE.MISDELIVERY_SORT_STATE,
  "misdeliverySortObject"
);
